
import { useAuth } from '@/contexts/AuthContext';
import { useUserModules, useCurrentUserModule } from '@/hooks/useUserModule';
import { CheckCircle, Clock, Trophy, ArrowRight, RefreshCw, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { resetUserProgress } from '@/api/module';
import { useState } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { ResponsiveContainer } from '@/components/ResponsiveContainer';
import { fetchFeaturedArticle } from '@/api/content';
import AdminJsonToggle from '@/components/AdminJsonToggle';

const Updates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: userModules, isLoading } = useUserModules();
  const { data: currentUserModule, isLoading: currentModuleLoading } = useCurrentUserModule();
  const [isResetting, setIsResetting] = useState(false);
  
  // Fetch featured article
  const { data: featuredArticle, isLoading: articleLoading } = useQuery({
    queryKey: ['featured-article'],
    queryFn: fetchFeaturedArticle,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Analyze module progress from backend data
  const getModuleProgress = () => {
    if (!userModules) return { profile: 'not-started', pulse: 'not-started', shape: 'not-started', coaching: 'not-started' };
    
    const progress = {
      profile: 'not-started',
      pulse: 'not-started', 
      shape: 'not-started',
      coaching: 'not-started'
    };

    userModules.forEach(userModule => {
      const moduleName = userModule.module.name.toLowerCase();
      const status = userModule.status;
      
      if (moduleName.includes('profile')) {
        progress.profile = status;
      } else if (moduleName.includes('pulse') && userModule.module.screenType === 'survey') {
        progress.pulse = status;
      } else if (moduleName.includes('shape') && userModule.module.screenType === 'survey') {
        progress.shape = status;
      } else if (moduleName.includes('coaching') || moduleName.includes('ai')) {
        progress.coaching = status;
      }
    });

    return progress;
  };

  const moduleProgress = getModuleProgress();
  
  // Calculate overall assessment status
  const getAssessmentStatus = () => {
    const pulseComplete = moduleProgress.pulse === 'completed';
    const shapeComplete = moduleProgress.shape === 'completed';
    const pulseInProgress = moduleProgress.pulse === 'in_progress';
    const shapeInProgress = moduleProgress.shape === 'in_progress';
    
    if (pulseComplete && shapeComplete) return 'completed';
    if (pulseInProgress || shapeInProgress || pulseComplete || shapeComplete) return 'in-progress';
    return 'not-started';
  };

  const assessmentStatus = getAssessmentStatus();
  
  // Use the same current module as DeclarativeModuleRouter for consistency
  const currentModule = currentUserModule;

  if (isLoading || currentModuleLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      <AdminJsonToggle 
        data={{
          user,
          userModules,
          currentUserModule,
          moduleProgress: getModuleProgress(),
          featuredArticle
        }}
        label="Updates Page Data"
      />
      {/* Header */}
      <div className="bg-black text-white p-4">
        <h1 className="text-lg font-semibold">Updates</h1>
      </div>

      {/* Content */}
      <ResponsiveContainer maxWidth="7xl" className="py-6 space-y-6">
        {/* Welcome Section */}
        <div className="text-center mb-8">
          <h2 className="text-xl lg:text-3xl font-semibold text-black mb-2">
            Welcome back, {user?.firstName || 'User'}!
          </h2>
          <p className="text-[#666666] lg:text-lg">
            Track your progress and see your latest updates here.
          </p>
        </div>

        {/* Desktop Grid Layout */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* Left Column - Progress Overview */}
          <div className="lg:col-span-2 space-y-6">

            {/* Current Activity */}
            {currentModule && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg lg:text-xl font-semibold text-blue-900 mb-2">Continue Your Journey</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-800 font-medium lg:text-lg">{currentModule.module.name}</p>
                    <p className="text-sm lg:text-base text-blue-600">
                      {currentModule.progressCheckpoint > 0 
                        ? `Question ${currentModule.progressCheckpoint + 1} in progress`
                        : 'Ready to start'
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/module')}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 lg:px-6 lg:py-3 rounded-md hover:bg-blue-700 transition-all hover:scale-105 text-sm lg:text-base"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Progress Overview */}
            <div className="bg-[#f7f7f7] rounded-lg p-4 lg:p-6 shadow-sm">
              <h3 className="text-lg lg:text-xl font-semibold text-black mb-4">Your Progress</h3>
              
              <div className="space-y-3 lg:space-y-4">
                {/* Render modules in sequence order */}
                {userModules
                  ?.sort((a, b) => a.module.sequence - b.module.sequence)
                  .map((userModule) => {
                    const module = userModule.module;
                    const status = userModule.status;
                    const isCompleted = status === 'completed';
                    const isInProgress = status === 'in_progress';
                    
                    // Determine icon and colors based on status
                    const getStatusIcon = () => {
                      if (isCompleted) {
                        return <CheckCircle className="h-5 w-5 text-green-600" />;
                      } else if (module.screenType === 'chat' && assessmentStatus === 'completed') {
                        return <Trophy className="h-5 w-5 text-blue-600" />;
                      } else if (module.screenType === 'chat') {
                        return <Trophy className="h-5 w-5 text-gray-400" />;
                      } else {
                        return <Clock className="h-5 w-5 text-gray-500" />;
                      }
                    };
                    
                    const getContainerColor = () => {
                      if (isCompleted) return 'bg-green-100';
                      if (isInProgress) return 'bg-yellow-100';
                      if (module.screenType === 'chat' && assessmentStatus === 'completed') return 'bg-blue-100';
                      return 'bg-gray-100';
                    };
                    
                    // Get display title and description
                    const getDisplayTitle = () => {
                      if (module.uiTitle) return module.uiTitle;
                      if (module.name.toLowerCase().includes('profile')) {
                        return isCompleted ? 'Profile Complete' : 'Profile Setup';
                      }
                      return module.name;
                    };
                    
                    const getDisplayDescription = () => {
                      if (module.uiDescription) return module.uiDescription;
                      
                      // Special handling for known module types
                      if (module.name.toLowerCase().includes('profile') && isCompleted) {
                        return `${user?.title || 'Professional'} at ${user?.department || 'Organization'}`;
                      } else if (module.name.toLowerCase().includes('profile')) {
                        return 'Complete your profile information';
                      } else if (module.name.toLowerCase().includes('pulse')) {
                        return 'Personality and leadership assessment';
                      } else if (module.name.toLowerCase().includes('shape')) {
                        return 'Skills and behavioral assessment';
                      } else if (module.screenType === 'chat') {
                        if (isCompleted) return 'Coaching sessions completed';
                        if (isInProgress) return 'Active coaching conversation';
                        if (assessmentStatus === 'completed') return 'Ready for personalized coaching';
                        return 'Complete assessments to unlock';
                      }
                      
                      return module.introText || '';
                    };
                    
                    // Skip non-display modules like welcome or intro
                    if (module.screenType === 'welcome' || module.screenType === 'survey_intro') {
                      return null;
                    }
                    
                    return (
                      <div key={userModule.id} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getContainerColor()}`}>
                          {getStatusIcon()}
                        </div>
                        <div className="flex-1">
                          <p className="text-black font-medium lg:text-lg">
                            {getDisplayTitle()}
                            {(isInProgress || isCompleted) && status !== 'not_started' && (
                              <span className="ml-2 text-sm font-normal">
                                {isCompleted ? ' - Complete' : ' - In Progress'}
                              </span>
                            )}
                          </p>
                          <p className="text-sm lg:text-base text-[#666666]">
                            {getDisplayDescription()}
                          </p>
                        </div>
                      </div>
                    );
                  })
                  .filter(Boolean)}
              </div>
            </div>
          </div>

          {/* Right Column - Article and Testing */}
          <div className="lg:col-span-1 space-y-6 mt-6 lg:mt-0">
            {/* Article Headline */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                 onClick={() => {
                   if (featuredArticle?.cta?.link) {
                     // If it's an internal link, navigate; otherwise open in new tab
                     if (featuredArticle.cta.link.startsWith('/')) {
                       navigate(featuredArticle.cta.link);
                     } else {
                       window.open(featuredArticle.cta.link, '_blank');
                     }
                   }
                 }}>
              {articleLoading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ) : featuredArticle ? (
                <>
                  <h3 className="text-2xl lg:text-3xl font-bold text-black leading-tight mb-2">
                    {featuredArticle.title}
                  </h3>
                  {featuredArticle.excerpt && (
                    <p className="text-sm lg:text-base text-[#666666] mb-3">
                      {featuredArticle.excerpt}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-4">
                      {featuredArticle.author && (
                        <span>By {featuredArticle.author}</span>
                      )}
                      {featuredArticle.readTime && (
                        <span>{featuredArticle.readTime}</span>
                      )}
                    </div>
                    {featuredArticle.cta && (
                      <span className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                        {featuredArticle.cta.text}
                        <ExternalLink className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                  {featuredArticle.tags && featuredArticle.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {featuredArticle.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3 className="text-2xl lg:text-3xl font-bold text-black leading-tight">
                    Welcome to Your Journey
                  </h3>
                  <p className="text-sm lg:text-base text-[#666666] mt-2">
                    Featured article
                  </p>
                </>
              )}
            </div>

            {/* Testing Actions - Available to all users */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 lg:p-5">
          <h4 className="text-sm font-semibold text-amber-900 mb-2">Testing Options</h4>
          <button
              onClick={async () => {
                if (confirm('This will reset all your module progress. Are you sure?')) {
                  setIsResetting(true);
                  try {
                    await resetUserProgress();
                    
                    // Clear all cached data
                    await queryClient.invalidateQueries();
                    
                    // Navigate to the first module (profile)
                    navigate('/module');
                  } catch (error) {
                    console.error('Error resetting progress:', error);
                    alert('Error resetting progress. Check console for details.');
                  } finally {
                    setIsResetting(false);
                  }
                }
              }}
            disabled={isResetting}
            className="text-amber-700 hover:text-amber-900 font-medium text-sm lg:text-base underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isResetting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Resetting...
              </>
            ) : (
              'Restart Test (Clear All Progress)'
            )}
          </button>
              <p className="text-xs lg:text-sm text-amber-700 mt-2">
                This will clear all your assessments and progress, allowing you to start fresh.
              </p>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default Updates;
